// src/hooks/useEnterpriseAI.js
// --- UPDATE ---------------------------------------------------------------------
// useAIModelPipeline
//   • submitRlhfGrade now THROWS when the server rejects the grade. It used to return null on
//     any failure (and never checked res.ok), so the review pages showed "Feedback Saved" for
//     grades that were never stored. Uses aiService.submitPredictionGrade (one grading path).
//   • loading covers grading as well as batch RUL; the state setters are guarded after unmount.
//   • getHeaders is module-level (it was recreated every render and missing from hook deps).
// useOllamaCopilot
//   • the EventSource is closed on unmount and when a new question starts (a second question
//     used to leave the first stream running and interleave both answers).
//   • stopStream() lets the UI cancel; error carries the reason; malformed chunks are skipped.
// --- END UPDATE ------------------------------------------------------------------
import { useState, useCallback, useEffect, useRef } from 'react';
import { useToast } from '@chakra-ui/react';
import { config } from '../config';
import { getAuthToken } from '../utils/auth'; // Import your token helper
// --- UPDATE ---
import { submitPredictionGrade } from '../services/aiService';
// --- END UPDATE ---

const API_BASE = config.apiUrl;

// --- UPDATE: module-level (stable) ---
const getHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${getAuthToken() || ''}`
});
// --- END UPDATE ---

export function useAIModelPipeline() {
  const [batchLoading, setBatchLoading] = useState(false);
  const [gradeInFlight, setGradeInFlight] = useState(0);
  const toast = useToast();
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const triggerBatchRul = useCallback(async (assetPayload) => {
    setBatchLoading(true);
    try {
      const res = await fetch(`${API_BASE}/ai/predict-rul/batch`, {
        method: 'POST',
        headers: getHeaders(), // ADDED AUTH
        body: JSON.stringify({ assets: assetPayload })
      });
      if (!res.ok) throw new Error(`Status ${res.status}`);
      const data = await res.json();
      // --- UPDATE: the backend answers 200 with queued:false / degraded:true when the ML engine is down
      if (data && (data.degraded || data.queued === false)) {
        toast({ title: 'Batch RUL not queued', description: data.message || 'The ML engine is unreachable.', status: 'warning', duration: 5000 });
      } else {
        toast({ title: 'Batch RUL Completed', status: 'success', duration: 3000 });
      }
      // --- END UPDATE ---
      return data;
    } catch (err) {
      toast({ title: 'ML Engine Connection Failed', description: err.message, status: 'error' });
      return null;
    } finally {
      if (mounted.current) setBatchLoading(false);
    }
  }, [toast]);

  /**
   * Records a human grade. Resolves with the server response; throws on failure so callers can
   * show an error instead of a false success.
   */
  const submitRlhfGrade = useCallback(async (predictionId, sourceTable, isCorrect, notes) => {
    setGradeInFlight(n => n + 1);
    try {
      return await submitPredictionGrade(predictionId, sourceTable, isCorrect, notes);
    } catch (err) {
      console.error('[RLHF Error]', err);
      throw err;
    } finally {
      if (mounted.current) setGradeInFlight(n => Math.max(0, n - 1));
    }
  }, []);

  return { triggerBatchRul, submitRlhfGrade, loading: batchLoading || gradeInFlight > 0 };
}

export function useOllamaCopilot() {
  const [chatResponse, setChatResponse] = useState('');
  const [streaming, setStreaming] = useState(false);
  // --- UPDATE ---
  const [error, setError] = useState(null);
  const sourceRef = useRef(null);
  const mounted = useRef(true);

  const closeStream = useCallback(() => {
    if (sourceRef.current) {
      try { sourceRef.current.close(); } catch (_) { /* already closed */ }
      sourceRef.current = null;
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; closeStream(); };
  }, [closeStream]);

  const stopStream = useCallback(() => {
    closeStream();
    if (mounted.current) setStreaming(false);
  }, [closeStream]);
  // --- END UPDATE ---

  const streamCopilotChat = useCallback((workOrderId, promptText) => {
    // --- UPDATE: one stream at a time ---
    closeStream();
    setError(null);
    // --- END UPDATE ---
    setChatResponse('');
    setStreaming(true);

    const token = getAuthToken();
    const queryParams = new URLSearchParams({
        workOrderId,
        prompt: promptText,
        token: token || '' // ADDED AUTH as query param for EventSource
    }).toString();

    const eventSource = new EventSource(`${API_BASE}/ai/copilot/chat/stream?${queryParams}`);
    sourceRef.current = eventSource;

    eventSource.addEventListener('chunk', (event) => {
      if (sourceRef.current !== eventSource || !mounted.current) return;
      try {
        const data = JSON.parse(event.data);
        if (data?.text) setChatResponse((prev) => prev + data.text);
      } catch (_) { /* skip malformed chunk */ }
    });

    eventSource.addEventListener('done', () => {
      if (sourceRef.current === eventSource) sourceRef.current = null;
      eventSource.close();
      if (mounted.current) setStreaming(false);
    });

    eventSource.addEventListener('error', (err) => {
      console.error('[Ollama Stream Error]', err);
      let message = 'The assistant stream was interrupted.';
      try { if (err?.data) message = JSON.parse(err.data)?.message || message; } catch (_) { /* not JSON */ }
      if (sourceRef.current === eventSource) sourceRef.current = null;
      eventSource.close();
      if (mounted.current) { setStreaming(false); setError(message); }
    });
  }, [closeStream]);

  return { chatResponse, streaming, streamCopilotChat, stopStream, error };
}
